<script setup lang="ts">
// Notificações do Minha Conta V2 (ENG-362) — tab raiz (sem screenTitle).
// Figma: só frame desktop 19771:52285 (mobile adaptado do mesmo layout).
// Dados reais: Smartico, mesmo fluxo da página antiga pages/user/notifications.vue
// (useSmarticoMessages → getInboxMessages + getInboxMessageBody por mensagem).
const { allInboxMessages } = storeToRefs(useSmarticoMessages());
const { loadMessages, loadSingleMessage, markNotificationAsRead } =
  useSmarticoMessages();

const { initSmarticoError } = storeToRefs(useSmarticoInit());
const { runNotificationAction } = useSmarticoInit();

// Toggle "Notificações de ofertas e mensagens" (Figma mobile 19771:42254, nó
// 23126:77614) — mesmo toggle real de marketing (PATCH bff/users/self-mkt)
// que já existe em ScreenProfile.vue/Header.vue, só que aqui SÓ no mobile
// (movido de ScreenAccountData.vue, que não tem mais esse card).
const {
  isActive: isMarketingActive,
  isSaving: isSavingMarketing,
  toggle: toggleMarketing,
} = useMarketingOptIn();

const FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'cashback', label: 'Cashback' },
  { key: 'bonus', label: 'Bônus' },
  { key: 'alerts', label: 'Alertas' },
  { key: 'favorites', label: 'Favoritos' },
];

const PER_PAGE = 6;

const activeFilter = ref<string>('all');
const page = ref<number>(1);
const isLoading = ref<boolean>(true);

// corpo (título/descrição/imagem) carregado por mensagem, cacheado por guid
const bodies = ref<Record<string, TInboxMessageBody | undefined>>({});
// marcadas como lidas nesta sessão (feedback imediato sem esperar reload)
const readManual = ref<Set<string>>(new Set());

async function loadAll() {
  isLoading.value = true;

  await loadMessages();

  // a API do Smartico não pagina o inbox: carrega o corpo de todas as
  // mensagens de uma vez (mesmo comportamento da página antiga, que
  // buscava o corpo de cada item renderizado)
  await Promise.all(
    allInboxMessages.value.map(async (message) => {
      if (!(message.message_guid in bodies.value)) {
        bodies.value[message.message_guid] = await loadSingleMessage(
          message.message_guid,
        );
      }
    }),
  );

  isLoading.value = false;
}

onMounted(() => {
  loadAll();
});

function isUnread(message: TInboxMessage) {
  return !message.read && !readManual.value.has(message.message_guid);
}

// algumas campanhas do Smartico mandam title/body como HTML completo,
// pensado pro widget de inbox NATIVO deles (com <style> próprio pra
// estilizar as classes .small-inbox-content etc.) — aqui é lista nossa, texto
// puro. Regex pra tirar tag é bypassável (tag splitting reconstrói <script>
// depois do replace) — usa o parser HTML real do browser em vez de regex.
// Só client: os dados chegam via onMounted, então no SSR title/body ainda
// estão vazios e nunca chegam a chamar isso de verdade.
function stripHtml(html: string) {
  if (!import.meta.client) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');

  // textContent atravessa a árvore inteira e inclui o texto de dentro de
  // <style>/<script> também (eles não deixam de ter nó de texto só por não
  // serem renderizados) — precisa remover antes, senão sobra CSS/JS cru.
  doc.querySelectorAll('style, script').forEach((el) => el.remove());

  return (doc.body.textContent || '').trim();
}

function titleOf(message: TInboxMessage) {
  const title = bodies.value[message.message_guid]?.title;

  return (title && stripHtml(title)) || 'Notificação';
}

function previewOf(message: TInboxMessage) {
  const body = bodies.value[message.message_guid];

  return stripHtml(body?.preview_body || body?.body || '');
}

function imageOf(message: TInboxMessage) {
  return bodies.value[message.message_guid]?.image || '';
}

// MOCK ENG-362: o Smartico não expõe categoria "Cashback" — classificação
// heurística por texto; "Bônus" usa o type 5 (BonusGiven, valor literal
// porque o enum InboxMessageType é ambient e não existe em runtime)
function categoryOf(message: TInboxMessage): 'cashback' | 'bonus' | 'alerts' {
  const body = bodies.value[message.message_guid];
  const text = `${body?.title || ''} ${body?.preview_body || ''}`.toLowerCase();

  if (text.includes('cashback')) {
    return 'cashback';
  }

  if (body?.type === 5) {
    return 'bonus';
  }

  return 'alerts';
}

const filteredMessages = computed<TInboxMessage[]>(() => {
  // não lidas primeiro (ordem estável dentro de cada grupo)
  const sorted = [...allInboxMessages.value].sort((a, b) => {
    return Number(isUnread(b)) - Number(isUnread(a));
  });

  if (activeFilter.value === 'all') {
    return sorted;
  }

  if (activeFilter.value === 'favorites') {
    return sorted.filter((message) => message.favorite);
  }

  return sorted.filter((message) => categoryOf(message) === activeFilter.value);
});

const lastPage = computed(() =>
  Math.max(1, Math.ceil(filteredMessages.value.length / PER_PAGE)),
);

const pagedMessages = computed(() =>
  filteredMessages.value.slice(
    (page.value - 1) * PER_PAGE,
    page.value * PER_PAGE,
  ),
);

watch(activeFilter, () => {
  page.value = 1;
});

function relativeTime(sentDate: string) {
  const parsed = new Date(sentDate.replace(' ', 'T'));

  if (Number.isNaN(parsed.getTime())) {
    return sentDate;
  }

  const minutes = Math.floor((Date.now() - parsed.getTime()) / 60000);

  if (minutes < 1) {
    return 'agora';
  }

  if (minutes < 60) {
    return `há ${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return hours === 1 ? 'há uma hora' : `há ${hours} horas`;
  }

  const days = Math.floor(hours / 24);

  return days === 1 ? 'há um dia' : `há ${days} dias`;
}

async function onMessageClick(message: TInboxMessage) {
  if (isUnread(message)) {
    readManual.value = new Set(readManual.value).add(message.message_guid);

    await markNotificationAsRead(message.message_guid);
    // atualiza contador global de não lidas (header/menu)
    loadMessages();
  }

  // dispara a ação da notificação — mesma regra do componente antigo
  // (runNotificationAction em stores/smartico/init.ts, extraída de
  // components/smartico/items/Notification.vue): atalhos de URL/missions/
  // levels/tournaments/store/nickname/avatar antes do SDK genérico. "dp:ok"
  // é só "marcar como lida", sem ação própria.
  const action = bodies.value[message.message_guid]?.action;

  if (action && action !== 'dp:ok') {
    runNotificationAction(action);
  }
}
</script>

<template>
  <!-- desktop: o box ocupa a altura toda e SÓ a lista rola (tabs e paginação fixas) -->
  <div
    class="border-solid flex flex-col md:h-full md:min-h-0 md:rounded-[24px] md:border md:border-v2-default/40 md:p-[16px]"
  >
    <p class="hidden pb-[12px] text-[14px] leading-[20px] text-white md:block">
      Notificações
    </p>

    <div
      class="border-solid flex w-full flex-col gap-[10px] md:mx-auto md:min-h-0 md:w-full md:max-w-[600px] md:flex-1 md:rounded-[24px] md:border md:border-v2-default/40 md:p-[16px]"
    >
      <UserV2FilterTabs
        v-model="activeFilter"
        :tabs="FILTERS"
      />

      <div
        v-if="isLoading"
        class="flex flex-col gap-[10px]"
      >
        <div
          v-for="n in 4"
          :key="`notification-skeleton-${n}`"
          class="notifications-skeleton h-[82px] w-full rounded-[8px] bg-v2-default/40"
        />
      </div>

      <p
        v-else-if="initSmarticoError"
        class="rounded-[10px] bg-v2-danger/20 p-[16px] text-center text-[12px] leading-[16px] text-v2-danger"
      >
        Não foi possível carregar as notificações. Tente novamente mais tarde.
      </p>

      <template v-else>
        <div
          v-if="pagedMessages.length"
          class="user-v2-scroll flex flex-col gap-[10px] md:min-h-0 md:flex-1 md:overflow-y-auto md:pr-[6px]"
        >
          <button
            v-for="message in pagedMessages"
            :key="message.message_guid"
            class="flex min-h-[82px] w-full items-start gap-[12px] rounded-[8px] border-l-[5px] pb-[14px] pl-[12px] pr-[15px] pt-[10px] text-left"
            :class="
              isUnread(message)
                ? 'border-v2-primary bg-gradient-to-l from-v2-primary/0 to-v2-primary/10'
                : 'border-transparent bg-gradient-to-l from-white/0 to-white/5'
            "
            type="button"
            @click="onMessageClick(message)"
          >
            <img
              v-if="imageOf(message)"
              alt=""
              class="h-[58px] w-[50px] shrink-0 self-center object-contain"
              :src="imageOf(message)"
            />
            <span
              v-else
              class="flex size-[44px] shrink-0 items-center justify-center self-center rounded-full bg-v2-default/40 text-white"
            >
              <svg
                class="size-[20px]"
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                viewBox="0 0 24 24"
              >
                <path
                  d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6Zm-4 9a2 2 0 0 1-4 0"
                />
              </svg>
            </span>

            <span class="flex min-w-0 flex-1 flex-col gap-[6px] self-center">
              <span class="text-[14px] font-bold leading-[16px] text-white">
                {{ titleOf(message) }}
              </span>
              <span
                class="notification-preview text-[12px] font-medium leading-[16px] text-white"
              >
                {{ previewOf(message) }}
              </span>
            </span>

            <span
              class="flex shrink-0 flex-col items-end justify-between self-stretch"
            >
              <span class="text-[10px] leading-[16px] text-white/30">
                {{ relativeTime(message.sent_date) }}
              </span>
              <span
                v-if="isUnread(message)"
                class="flex items-center gap-[5px] text-[10px] leading-[16px] text-white"
              >
                <span class="size-[7px] rounded-full bg-white" />
                Novo
              </span>
              <span
                v-else
                class="flex items-center gap-[4px] text-[10px] leading-[16px] text-white/40"
              >
                <svg
                  class="size-[12px]"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path d="m2 13 4 4L14 7m-5 10 2 2 8-10" />
                </svg>
                Lida
              </span>
            </span>
          </button>
        </div>

        <p
          v-else
          class="rounded-[10px] bg-v2-default/40 p-[16px] text-center text-[12px] leading-[16px] text-v2-default-800"
        >
          Nenhuma notificação encontrada.
        </p>

        <UserV2Pagination
          v-if="lastPage > 1"
          :last-page="lastPage"
          :page="page"
          @change="(newPage) => (page = newPage)"
        />
      </template>

      <!-- Notificações de ofertas e mensagens: só no mobile (Figma
           19771:42254 ativado / 23227:78333 desativado) — o toggle real de
           marketing saiu de ScreenAccountData.vue e mora só aqui agora. -->
      <div
        class="flex w-full items-center gap-[16px] rounded-[16px] p-[12px] md:hidden"
        :class="isMarketingActive ? 'bg-v2-success-50' : 'bg-v2-danger/15'"
      >
        <p class="flex-1 text-[14px] leading-[20px] text-v2-foreground">
          {{
            isMarketingActive
              ? 'Deixe ativado para receber ofertas, giros grátis, promoções e novidades em primeira mão.'
              : 'Suas notificações estão desativadas, para voltar a receber promoções e ofertas ative-as novamente.'
          }}
        </p>
        <button
          :aria-checked="isMarketingActive"
          class="flex h-[24px] w-[40px] shrink-0 items-center rounded-full p-[4px]"
          :class="[
            isMarketingActive
              ? 'justify-end bg-v2-success'
              : 'justify-start bg-v2-default',
            isSavingMarketing ? 'opacity-60' : '',
          ]"
          :disabled="isSavingMarketing"
          role="switch"
          type="button"
          @click="toggleMarketing"
        >
          <span
            class="size-[16px] rounded-full bg-white shadow-[0px_2px_10px_0px_rgba(0,0,0,0.06),0px_0px_1px_0px_rgba(0,0,0,0.3)]"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// main.scss reseta `transition` — skeleton pulsa via keyframes
.notifications-skeleton {
  animation: notifications-pulse 1.2s ease-in-out infinite;
}

@keyframes notifications-pulse {
  0%,
  100% {
    opacity: 0.45;
  }

  50% {
    opacity: 1;
  }
}

.notification-preview {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
